import { Controller } from "@hotwired/stimulus";

// GR-3354: the room block on a hotel quote is attendance times nights at the
// room rate, and two of those three numbers are typed into Hotel Details at
// the top of the same form. GR-3372 moved that block above Line Items for
// exactly this reason, so the form reads top to bottom: pick the hotel, state
// the stay and the rate, and the room block below follows.
//
// The server prefills the line from the request's own dates and headcount
// (QuotePrefill). This keeps it true afterwards, because a stay typed off what
// the hotel actually held is routinely not the stay that was requested, and
// retyping the multiplication by hand is the arithmetic this ticket exists to
// stop.
//
// What it will not do is overwrite a number a person typed. A field is the
// controller's to rewrite only while it holds what the controller last wrote
// (or the server's prefill, on a form that has not been saved yet); the moment
// somebody types their own quantity or their own negotiated rate into the
// line, that number is theirs and the recompute stops touching it. On an edit
// form nothing is adopted at all: those numbers were agreed with the hotel.
export default class HotelRoomBlockController extends Controller {
  static values = {
    attendance: Number,
    prefilled: Boolean,
    category: { type: String, default: "rooming" },
    // GR-3893: only orgs with AV sourcing get the F&B minimum line.
    fbMinimumLine: Boolean,
  };

  // The hotel inputs are found by id rather than by target, so the fieldset
  // GR-3372 wrote stays exactly as it wrote it: their names and ids are the
  // stable part, and a quote form without them (any type but accommodation)
  // simply has nothing to listen to.
  static HOTEL_FIELDS = [
    "#billing_invoice_room_rate",
    "#billing_invoice_check_in_date",
    "#billing_invoice_check_out_date",
  ];

  connect() {
    this.written = {};
    this.sync = this.sync.bind(this);
    this.hotelFields().forEach((field) => {
      field.addEventListener("input", this.sync);
      field.addEventListener("change", this.sync);
    });
    if (this.prefilledValue) this.adopt();

    this.syncMinimum = this.syncMinimum.bind(this);
    this.minimumField()?.addEventListener("input", this.syncMinimum);
    this.minimumField()?.addEventListener("change", this.syncMinimum);
    // After the line-items controller inside this element has connected, so a
    // form opened with a minimum and no line (an older quote) shows the line.
    Promise.resolve().then(this.syncMinimum);
  }

  disconnect() {
    this.hotelFields().forEach((field) => {
      field.removeEventListener("input", this.sync);
      field.removeEventListener("change", this.sync);
    });
    this.minimumField()?.removeEventListener("input", this.syncMinimum);
    this.minimumField()?.removeEventListener("change", this.syncMinimum);
  }

  // GR-3851: the room rate writes the rate and nothing else. Only the stay
  // changes how many room nights the block holds.
  static RATE_FIELD = "#billing_invoice_room_rate";

  hotelFields() {
    return this.constructor.HOTEL_FIELDS.map((selector) =>
      this.element.querySelector(selector)
    ).filter(Boolean);
  }

  // What the server prefilled is the controller's to keep up to date: the
  // person has not touched it yet, and it was written by this same arithmetic.
  //
  // GR-3851: a quantity is adopted only when it is what this arithmetic gives.
  // A request with a room block on its schedule is prefilled with the room
  // nights it asked for, which is not attendance times nights, and that number
  // is the request's, not the controller's.
  adopt() {
    const row = this.roomBlockRow();
    if (!row) return;

    const quantity = this.lineField(row, "quantity");
    if (quantity && quantity.value === String(this.roomCount())) this.written.quantity = quantity.value;

    const rate = this.lineField(row, "unit_price");
    if (rate) this.written.unit_price = rate.value;
  }

  sync(event) {
    const row = this.roomBlockRow();
    if (!row) return;

    if (!event?.target?.matches?.(this.constructor.RATE_FIELD)) {
      this.apply(row, "quantity", this.roomCount());
    }
    this.apply(row, "unit_price", this.roomRate());
  }

  apply(row, name, value) {
    const field = this.lineField(row, name);
    if (!field || value === null) return;
    if (field.value !== "" && field.value !== this.written[name]) return;

    field.value = value;
    this.written[name] = String(value);
    // The quoted total is the rows added up and each row recalculates on its
    // own input event, so a line rewritten in silence would leave the total
    // disagreeing with the line above it.
    field.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // One room per attendee for every night of the stay. Without a headcount
  // there is no room count to write, and a stay nobody has filled in yet is
  // not zero nights, it is no answer.
  roomCount() {
    const nights = this.nights();
    if (nights === null || this.attendanceValue < 1) return null;

    return this.attendanceValue * nights;
  }

  nights() {
    const checkIn = this.dateValue("#billing_invoice_check_in_date");
    const checkOut = this.dateValue("#billing_invoice_check_out_date");
    if (checkIn === null || checkOut === null) return null;

    const nights = Math.round((checkOut - checkIn) / 86400000);
    return nights > 0 ? nights : null;
  }

  dateValue(selector) {
    const raw = this.element.querySelector(selector)?.value;
    if (!raw) return null;

    const parsed = Date.parse(`${raw}T00:00:00Z`);
    return Number.isNaN(parsed) ? null : parsed;
  }

  roomRate() {
    return this.element.querySelector("#billing_invoice_room_rate")?.value || null;
  }

  // The room block is the line filed under the room-block category, rather
  // than a row the server had to mark: a row somebody has recategorised is no
  // longer the room block, and one they recategorised into it is.
  roomBlockRow() {
    return Array.from(this.element.querySelectorAll(".quote-line")).find((row) => {
      if (row.hidden) return false;

      return row.querySelector("select[name$='[cost_category]']")?.value === this.categoryValue;
    });
  }

  lineField(row, name) {
    return row.querySelector(`input[name$='[${name}]']`);
  }

  // GR-3855: the F&B minimum is a catering line on the quote, one of it at the
  // minimum (HotelPriceBook::FB_MINIMUM). It follows the field as it is typed
  // and is gone when the field is emptied. The row is written and removed
  // through the line-items controller, so it comes from the same template as
  // Add Line and a recorded one is marked for destruction rather than lost.
  static FB_MINIMUM = "Food and beverage minimum";

  minimumField() {
    return this.element.querySelector("#billing_invoice_food_beverage_minimum");
  }

  syncMinimum() {
    if (!this.fbMinimumLineValue) return;

    const lineItems = this.lineItems();
    if (!lineItems || !this.minimumField()) return;

    const amount = this.minimumField().value;
    let row = this.minimumRow();

    if (amount === "") {
      if (row) lineItems.removeRow({ target: row });
      return;
    }

    row ??= this.newMinimumRow(lineItems);
    this.lineField(row, "unit_price").value = amount;
    lineItems.recalculate();
  }

  newMinimumRow(lineItems) {
    const row = lineItems.blankRow() ?? lineItems.appendRow();
    lineItems.selectCategory(row, "catering");
    this.lineField(row, "description").value = this.constructor.FB_MINIMUM;
    this.lineField(row, "description").title = this.constructor.FB_MINIMUM;
    this.lineField(row, "quantity").value = "1";
    return row;
  }

  minimumRow() {
    return Array.from(this.element.querySelectorAll(".quote-line")).find(
      (row) => !row.hidden && this.lineField(row, "description")?.value === this.constructor.FB_MINIMUM
    );
  }

  lineItems() {
    const element = this.element.querySelector("[data-controller~='quote-line-items']");
    if (!element) return null;

    return this.application.getControllerForElementAndIdentifier(element, "quote-line-items");
  }
}
